/**
 * Human-readable pending workspace changes for cancel/publish confirmations.
 *
 * Prefer the GraphQL `userTagWorkspacePendingChangeSummary` (single source of truth).
 * This file remains as a **fallback** when the summary field is not available, using:
 * - Added: modules created after the active snapshot time (if any)
 * - Removed: not represented client-side; use the server field for staged deletes
 */
export type ModuleForPendingSummary = {
  id: string;
  name: string;
  createdAt: string;
  hasDraft: boolean;
  hasPendingToggle: boolean;
  enabled: boolean;
  draftEnabled: boolean | null;
  effectiveEnabled: boolean;
};

export type LabelledModuleChange = {
  moduleName: string;
  fromLabel: string;
  toLabel: string;
};

export type WorkspacePendingSummary = {
  codeDraftModuleNames: string[];
  toggleChanges: LabelledModuleChange[];
  /** Reserved 配信条件連動 link changes; from/to are the linked group names */
  linkChanges: LabelledModuleChange[];
  addedModuleNames: string[];
  /** Only populated in fallback mode; use server summary for staged deletions */
  removedModuleNames: string[];
};

const onOff = (v: boolean) => (v ? "有効" : "無効");

export function buildWorkspacePendingSummary(
  modules: readonly ModuleForPendingSummary[],
  activeSnapshotCreatedAt: string | null | undefined
): WorkspacePendingSummary {
  const codeDraftModuleNames = modules
    .filter((m) => m.hasDraft)
    .map((m) => m.name);

  const toggleChanges = modules
    .filter((m) => m.hasPendingToggle)
    .map((m) => ({
      moduleName: m.name,
      fromLabel: onOff(m.enabled),
      toLabel: onOff(m.effectiveEnabled),
    }));

  let addedModuleNames: string[] = [];
  if (activeSnapshotCreatedAt) {
    const t = new Date(activeSnapshotCreatedAt).getTime();
    addedModuleNames = modules
      .filter((m) => new Date(m.createdAt).getTime() > t)
      .map((m) => m.name);
  }

  return {
    codeDraftModuleNames,
    toggleChanges,
    // Reserved link changes are only known server-side.
    linkChanges: [],
    addedModuleNames,
    removedModuleNames: [],
  };
}
